import { DropdownEditor, Field } from '@approvalmax/ui';
import { dataProviders } from 'modules/common';
import { domain } from 'modules/data';
import { memo } from 'react';

import { PLACEHOLDER_FIELD } from '../../RequestSearchPopup.constants';
import { Header, WrapFieldRow } from '../../RequestSearchPopup.styles';
import { NetSuiteCustomSearchFields } from '../NetSuiteCustomSearchFields/NetSuiteCustomSearchFields';
import { messages } from './NetSuiteLinesSearchFields.messages';
import { NetSuiteLinesSearchFieldsProps } from './NetSuiteLinesSearchFields.types';

export const NetSuiteLinesSearchFields = memo<NetSuiteLinesSearchFieldsProps>((props) => {
    const { integrationCode, integrationEnabled, params, updateParameter, disabled } = props;

    if (
        !integrationCode ||
        [domain.IntegrationCode.NetSuiteBillPayment, domain.IntegrationCode.NetSuiteVendor].includes(integrationCode)
    ) {
        return null;
    }

    const isExpenseReport = integrationCode === domain.IntegrationCode.NetSuiteExpenseReport;
    const isSalesOrderOrInvoice = [
        domain.IntegrationCode.NetSuiteSalesOrder,
        domain.IntegrationCode.NetSuiteInvoice,
    ].includes(integrationCode);
    const isJournalEntry = integrationCode === domain.IntegrationCode.NetSuiteJournalEntry;
    const isItemReceipt = integrationCode === domain.IntegrationCode.NetSuiteItemReceipt;

    return (
        <>
            <Header>
                {(() => {
                    switch (integrationCode) {
                        case domain.IntegrationCode.NetSuiteExpenseReport:
                            return messages.netSuiteExpensesBlockHeader;

                        case domain.IntegrationCode.NetSuiteSalesOrder:
                        case domain.IntegrationCode.NetSuiteInvoice:
                            return messages.netSuiteItemsBlockHeader;

                        case domain.IntegrationCode.NetSuiteJournalEntry:
                            return messages.netSuiteLinesBlockHeader;

                        default:
                            return messages.netSuiteItemsAndExpensesBlockHeader;
                    }
                })()}
            </Header>

            <WrapFieldRow>
                {!isSalesOrderOrInvoice && !isJournalEntry && !isItemReceipt && (
                    <Field title={messages.netSuiteCategoryFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteExpenseCategory
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteCategoryFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteCategory}
                                onChange={updateParameter('netSuiteCategory')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {!isSalesOrderOrInvoice && (
                    <Field title={messages.netSuiteAccountFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteAccount
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteAccountFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteAccount}
                                onChange={updateParameter('netSuiteAccount')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {!isExpenseReport && !isJournalEntry && (
                    <Field title={messages.netSuiteItemFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteItem
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteItemFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteItem}
                                onChange={updateParameter('netSuiteItem')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {!isItemReceipt && (
                    <Field title={messages.netSuiteDepartmentFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteDepartment
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteDepartmentFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteDepartment}
                                onChange={updateParameter('netSuiteDepartment')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {!isItemReceipt && (
                    <Field title={messages.netSuiteClassFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteClass
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteClassFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteClass}
                                onChange={updateParameter('netSuiteClass')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                <Field title={messages.netSuiteLocationFieldTitle}>
                    <dataProviders.FieldDataProvider
                        integrationCode={integrationCode}
                        field={
                            params.company?.fields.find(
                                (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteLocation
                            ) || PLACEHOLDER_FIELD
                        }
                    >
                        <DropdownEditor
                            placeholder={messages.netSuiteLocationFieldPlaceholder}
                            disabled={disabled || !integrationEnabled}
                            value={params.netSuiteLocation}
                            onChange={updateParameter('netSuiteLocation')}
                        />
                    </dataProviders.FieldDataProvider>
                </Field>

                {!isSalesOrderOrInvoice && !isJournalEntry && !isItemReceipt && (
                    <Field
                        title={
                            isExpenseReport
                                ? messages.netSuiteCustomerFieldTitleExpenseReport
                                : messages.netSuiteCustomerFieldTitle
                        }
                    >
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteCustomer
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteCustomerFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteCustomer}
                                onChange={updateParameter('netSuiteCustomer')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {!isItemReceipt && (
                    <Field title={messages.netSuiteTaxCodeFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteTaxCode
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteTaxCodeFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteTaxCode}
                                onChange={updateParameter('netSuiteTaxCode')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                <NetSuiteCustomSearchFields
                    params={params}
                    integrationCode={integrationCode}
                    integrationEnabled={integrationEnabled}
                    disabled={disabled}
                    updateParameter={updateParameter}
                    isLineCustomFields
                />
            </WrapFieldRow>
        </>
    );
});

NetSuiteLinesSearchFields.displayName = 'NetSuiteLinesSearchFields';
